import AsyncStorage from "@react-native-async-storage/async-storage";

import { captureMessage, setTag } from "@/sentry";

export class Storage {
  /** Mirrors device storage so reads survive its failure. Lost on reload, by design. */
  private inMemoryFallback = new Map<string, string>();
  private isStorageAvailable = true;

  /** Runs a device-storage operation. Failure switches us to memory only for the rest of the session */
  private async attempt<T>(
    operation: string,
    run: () => Promise<T>,
  ): Promise<T | undefined> {
    if (!this.isStorageAvailable) return;
    try {
      return await run();
    } catch (error) {
      this.isStorageAvailable = false;
      setTag("storage.unavailable", true);
      captureMessage("Device storage unavailable", {
        level: "warning",
        extra: { operation, error },
      });
    }
  }

  async get<T>(key: string, fallback?: T): Promise<T | undefined> {
    const stored = await this.attempt("getItem", () =>
      AsyncStorage.getItem(key),
    );
    if (this.isStorageAvailable) {
      if (stored) {
        // Mirror what's stored on device into memory in case of future failure/revocation of localstorage
        this.inMemoryFallback.set(key, stored);
      } else {
        // Key was removed from device storage, also remove from memory
        this.inMemoryFallback.delete(key);
      }
    }
    const serializedValue = this.inMemoryFallback.get(key);
    if (!serializedValue) return fallback;
    try {
      return JSON.parse(serializedValue) as T;
    } catch (_) {
      return fallback;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    const serializedValue = JSON.stringify(value);
    this.inMemoryFallback.set(key, serializedValue);
    await this.attempt("setItem", () =>
      AsyncStorage.setItem(key, serializedValue),
    );
  }

  async remove(key: string): Promise<void> {
    this.inMemoryFallback.delete(key);
    await this.attempt("removeItem", () => AsyncStorage.removeItem(key));
  }

  async getAllKeys(): Promise<readonly string[]> {
    const keys =
      (await this.attempt("getAllKeys", () => AsyncStorage.getAllKeys())) ?? [];
    return Array.from(new Set([...keys, ...this.inMemoryFallback.keys()]));
  }

  getMeta<T>(key: string, fallback?: T): () => Promise<T | undefined> {
    return () => this.get<T>(key, fallback);
  }

  setMeta<T>(key: string): (value: T) => Promise<void> {
    return (value: T) => this.set(key, value);
  }

  removeMeta(key: string): () => Promise<void> {
    return () => this.remove(key);
  }

  getAllKeysMeta(): () => Promise<readonly string[]> {
    return () => this.getAllKeys();
  }
}

export default new Storage();
